import { inject } from 'vue'
import { fcm } from './fcm'
import { barcode } from './barcode'
import { useAppStore } from '@/stores/appStore'
import * as Sentry from '@sentry/vue'
import { Capacitor } from '@capacitor/core'
import router from '@/router'

const appStartTime = Date.now();

let loadingWatchdogTimer: ReturnType<typeof setTimeout> | null = null;

export const eventListenerService = {
    initialize() {
        const eventBus = inject('emitter') as GlobalEmitter;
        const appStore = useAppStore();

        //  ****************************************************
        //  below here all events to which listening is required
        //  ****************************************************

        eventBus.on('snackbar', (data) => {
            console.debug('snackbar event received!', data);
            appStore.showSnackbar(data as snackevent);
        });

        eventBus.on('loading', (data) => {
            appStore.setLoading(data as boolean);
            if (data) {
                clearTimeout(loadingWatchdogTimer);
                loadingWatchdogTimer = setTimeout(() => {
                    appStore.setLoading(false);
                }, 17000);
            } else {
                if (loadingWatchdogTimer !== null) {
                    clearTimeout(loadingWatchdogTimer);
                    loadingWatchdogTimer = null;
                }
            }
        });

        eventBus.on('genericLoadingError', (error) => {
            appStore.showGenericError("Error loading data.", error as snackevent['error']);
        });

        eventBus.on('genericCreateError', (error) => {
            appStore.showGenericError("Error, could not create the resource.", error as snackevent['error']);
        });

        eventBus.on('genericUpdateError', (error) => {
            appStore.showGenericError("Error, could not update the resource.", error as snackevent['error']);
        });

        eventBus.on('genericSavedSuccess', () => {
            appStore.showGenericSuccess("Resource saved succesfully.");
        });

        eventBus.on('genericSavedError', (error) => {
            appStore.showGenericError("Resource could not be saved.", error as snackevent['error']);
        });

        eventBus.on('genericSaveError', (error) => {
            appStore.showGenericError("Resource could not be saved.", error as snackevent['error']);
        });

        eventBus.on('genericDeleteSuccess', () => {
            appStore.showGenericSuccess("Resource deleted succesfully.");
        });

        eventBus.on('genericDeleteError', (error) => {
            appStore.showGenericError("Resource could not be removed.", error as snackevent['error']);
        });

        eventBus.on('barcode-scanned', (data) => {
            const value = data as string;
            console.debug('new  barcode received!', `value: ${value}`);
            barcode.handleScannedBarcode(value)
        })
        eventBus.on('logout', () => {
            fcm.unsubscribeFromFCM()
        })
        eventBus.on('notification', (data) => {
            fcm.scheduleNotification(data as unknown[])
        })
        eventBus.on('removePushNotification', (data) => {
            fcm.removePushNotification(data as { id: string | number })
        })
        eventBus.on('subscribeToTestFCM', (data) => {
            fcm.subscribeToTestFCMChannel(data as boolean)
        });
        eventBus.on('request-timeout', (data) => {
            const timeoutData = data as {
                url?: string;
                method?: string;
                duration_ms?: number;
                pendingRequestCount?: number;
                traceId?: string;
            };

            Sentry.captureMessage('Axios request timeout', {
                level: 'warning',
                extra: {
                    timedOutUrl: timeoutData.url,
                    timedOutMethod: timeoutData.method,
                    timeoutMs: 15000,
                    platform: Capacitor.getPlatform(),
                    duration_ms: timeoutData.duration_ms,
                    pendingRequestCount: timeoutData.pendingRequestCount,
                    traceId: timeoutData.traceId,
                    pageRoute: router.currentRoute.value.name,
                    sessionDuration_s: Math.floor((Date.now() - appStartTime) / 1000),
                },
            });

            // A timeout is a network event, not an auth failure — do NOT clear tokens or reload.
            // The 17s loading watchdog (above) clears any stuck loading state automatically.
            eventBus.emit('snackbar', {
                snackbarText: '⚠️ Request timed out. Please refresh if the issue persists.',
                snackbarTimeout: 8000,
            });
        });
        //  ****************************************************
        //  above here all events to which listening is required
        //  ****************************************************
        console.debug("event listener initialized")
    },
}

export default eventListenerService
